import React from 'react'

import { CircularProgress, alpha } from '@mui/material'

/**
 * The indeterminate ring that RIDES A BADGE'S OWN BORDER while a request is in
 * flight.
 *
 * Extracted for VERA-304 (§36 — the second hand-roll becomes a shared component).
 * `SuccessState` built it for the claim-ownership confirmation; `ScanTag` needed
 * the identical treatment on its 88px scan chip, and these two are the same badge
 * in two flows. Its own comment already said as much — "the same fix, same
 * reasoning, same numbers as the scan chip in ScanTag — the two are the same badge
 * in two flows and must not drift again" — while remaining a private copy that
 * could.
 *
 * ## The geometry, which is the whole reason this is not a bare CircularProgress
 *
 * The ring is NOT a smaller spinner floating inside the badge. It traces the
 * badge's border, so:
 *
 *  - `size` is the badge's own outer diameter, not something smaller. A ring at
 *    (badge - 8) reads as a second, concentric circle rather than as this badge
 *    being busy — the failure `ScanTag`'s history records at step 6.
 *  - `thickness` is relative to CircularProgress's own 44-unit viewBox, so it has
 *    to come DOWN as the size goes up to keep the rendered stroke constant. The
 *    default 1.1 at 88px is `1.1 * 88/44` ≈ 2.2px; it is derived from `size` here
 *    so a caller with a different badge gets the same stroke rather than a
 *    thicker or thinner one.
 *  - Positioned `absolute` + `inset: 0` + `margin: auto`, never left in the flex
 *    flow: a parent with a 1px border has an 86px content box, so an 88px flex
 *    child overflows it and sits a pixel off. Centring by margin against the
 *    PADDING box puts the ring exactly on the border. Not a translate — MUI
 *    animates the root's `transform` for the rotation and would overwrite it.
 *
 * The caller must therefore be `position: relative`. Both current callers are.
 *
 * ## Why an indeterminate ring is honest HERE and was not on the armed scan chip
 *
 * `ScanTag` removed an indeterminate spinner from its ARMED state seven times over,
 * and the standing reason is good: nothing advances while the app waits for the
 * user to move their phone, so a spinner promises progress that does not exist.
 * That argument does not reach this state. Once a token has been handed to the
 * server, a request genuinely IS in flight and will genuinely resolve without the
 * user doing anything — which is exactly what an indeterminate ring says.
 */
export interface PendingRingProps {
  /** The badge's outer diameter. The ring traces its border, so this matches it. */
  size: number
}

/** The stroke, in CSS pixels, the ring should render at whatever `size` it takes. */
const STROKE_PX = 2.2

export const PendingRing: React.FC<PendingRingProps> = ({ size }) => (
  <CircularProgress
    size={size}
    // `thickness` is in viewBox units (44), so px / (size / 44) holds the rendered
    // stroke constant across badge sizes.
    thickness={STROKE_PX / (size / 44)}
    sx={theme => ({
      position: 'absolute',
      inset: 0,
      margin: 'auto',
      color: alpha(theme.palette.text.primary, 0.35),
      // Stop BOTH halves of the indeterminate animation — the root's rotation and
      // the circle's dash sweep — leaving a static arc rather than a still ring
      // that says nothing.
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
        '& .MuiCircularProgress-circle': { animation: 'none' },
      },
    })}
  />
)

export default PendingRing
